import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Context,
  type ReactNode,
} from 'react';
import type {
  AccentId,
  DensityId,
  GridId,
  MotionId,
  RadiusId,
  RenderingId,
  ShadowId,
  SurfaceStyleId,
  ThemeContextValue,
  ThemeMode,
  ThemeState,
  TypeScaleId,
} from './types.js';
import { DEFAULT_THEME_STATE } from './types.js';
import { mergeDefaults, readState, writeState, type StorageMode } from './storage.js';
import { applyThemeAttributes, resolveTheme, transitionTheme } from './apply.js';

export const ThemeContext: Context<ThemeContextValue | null> =
  createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeMode;
  defaultAccent?: AccentId;
  defaultDensity?: DensityId;
  defaultGrid?: GridId;
  defaultRadius?: RadiusId;
  defaultSurfaceStyle?: SurfaceStyleId;
  defaultShadow?: ShadowId;
  defaultTypeScale?: TypeScaleId;
  defaultRendering?: RenderingId;
  defaultMotion?: MotionId;
  storage?: StorageMode;
  storageKey?: string;
  onChange?: (state: ThemeState) => void;
}

export function ThemeProvider(props: ThemeProviderProps): ReactNode {
  const storage = props.storage ?? 'localStorage';
  const storageKey = props.storageKey ?? 'viquard.theme';

  const initial = useMemo<ThemeState>(() => {
    const base: ThemeState = {
      ...DEFAULT_THEME_STATE,
      theme: props.defaultTheme ?? DEFAULT_THEME_STATE.theme,
      accent: props.defaultAccent ?? DEFAULT_THEME_STATE.accent,
      density: props.defaultDensity ?? DEFAULT_THEME_STATE.density,
      grid: props.defaultGrid ?? DEFAULT_THEME_STATE.grid,
      radius: props.defaultRadius ?? DEFAULT_THEME_STATE.radius,
      surfaceStyle: props.defaultSurfaceStyle ?? DEFAULT_THEME_STATE.surfaceStyle,
      shadow: props.defaultShadow ?? DEFAULT_THEME_STATE.shadow,
      typeScale: props.defaultTypeScale ?? DEFAULT_THEME_STATE.typeScale,
      rendering: props.defaultRendering ?? DEFAULT_THEME_STATE.rendering,
      motion: props.defaultMotion ?? DEFAULT_THEME_STATE.motion,
    };
    return mergeDefaults({ ...base, ...readState(storage, storageKey) });
  }, [
    props.defaultTheme,
    props.defaultAccent,
    props.defaultDensity,
    props.defaultGrid,
    props.defaultRadius,
    props.defaultSurfaceStyle,
    props.defaultShadow,
    props.defaultTypeScale,
    props.defaultRendering,
    props.defaultMotion,
    storage,
    storageKey,
  ]);

  const [state, setInternal] = useState<ThemeState>(initial);
  const firstApply = useRef(true);
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;

  useEffect(() => {
    if (firstApply.current) {
      applyThemeAttributes(state);
      firstApply.current = false;
      return;
    }
    transitionTheme(() => {
      applyThemeAttributes(state);
    });
    writeState(storage, storageKey, state);
    onChangeRef.current?.(state);
  }, [state, storage, storageKey]);

  // listen for OS color-scheme changes while theme === 'system'
  useEffect(() => {
    if (state.theme !== 'system') return;
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (): void => {
      applyThemeAttributes(state);
    };
    mq.addEventListener('change', handler);
    return () => {
      mq.removeEventListener('change', handler);
    };
  }, [state]);

  const update = useCallback((patch: Partial<ThemeState>) => {
    setInternal((prev) => ({ ...prev, ...patch }));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...state,
      resolvedTheme: resolveTheme(state.theme),
      setTheme: (t) => {
        update({ theme: t });
      },
      setAccent: (a, hex) => {
        // `exactOptionalPropertyTypes: true` requires the optional
        // `customAccent` key to be absent when unset — spread it
        // conditionally instead of passing `undefined`.
        if (a === 'custom') {
          update({ accent: a, ...(hex !== undefined ? { customAccent: hex } : {}) });
        } else {
          update({ accent: a });
        }
      },
      setDensity: (d) => {
        update({ density: d });
      },
      setGrid: (g) => {
        update({ grid: g });
      },
      setRadius: (r) => {
        update({ radius: r });
      },
      setSurfaceStyle: (s) => {
        update({ surfaceStyle: s });
      },
      setShadow: (s) => {
        update({ shadow: s });
      },
      setTypeScale: (t) => {
        update({ typeScale: t });
      },
      setRendering: (r) => {
        update({ rendering: r });
      },
      setMotion: (m) => {
        update({ motion: m });
      },
      setState: update,
      reset: () => {
        setInternal(DEFAULT_THEME_STATE);
      },
    }),
    [state, update],
  );

  return <ThemeContext.Provider value={value}>{props.children}</ThemeContext.Provider>;
}

/** Bootstrap script to place in <head> to avoid FOUC. */
export function renderThemeBootstrap(storageKey: string = 'viquard.theme'): string {
  const key = JSON.stringify(storageKey);
  return `(function(){try{var k=${key};var raw=localStorage.getItem(k);var s=raw?JSON.parse(raw):{};var t=s.theme||'system';if(t==='system'){t=matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';}var r=document.documentElement;r.setAttribute('data-theme',t);r.setAttribute('data-accent',s.accent||'ochre');r.setAttribute('data-density',s.density||'comfortable');r.setAttribute('data-grid',s.grid||'dots');r.setAttribute('data-radius',s.radius||'pillowy');r.setAttribute('data-surface',s.surfaceStyle||'floating');r.setAttribute('data-shadow',s.shadow||'soft');r.setAttribute('data-typescale',s.typeScale||'standard');r.setAttribute('data-rendering',s.rendering||'balanced');r.setAttribute('data-motion',s.motion||'standard');}catch(e){}})();`;
}
